/**
 * The page's replacement for the shared scene's `realistic/bakedTreeData.ts`
 * (vite.config.ts maps that module here). The phone bundles the two baked
 * tree sets statically; on the page they are 8 MB of vertex data that would
 * otherwise sit in the first bundle, so each set is fetched the first time a
 * biome needs it (scenePreload.ts) and registered here. The getters are the
 * package's contract: synchronous, called while a biome's vegetation is
 * meshed, so a set must be loaded before that biome mounts. PuttStage holds
 * the scene on its loading layer (or on the previous biome) until then.
 */
import type { BakedTree } from '../../../packages/putt-scene/src/realistic/bakedTree';
import cedarTreesUrl from '../../../packages/putt-scene/assets/cedar-trees-v1.json?url';
import woodlandTreesUrl from '../../../packages/putt-scene/assets/woodland-trees-v1.json?url';

export type BakedTreeSet = 'cedar' | 'woodland';

const SET_URLS: Readonly<Record<BakedTreeSet, string>> = {
  cedar: cedarTreesUrl,
  woodland: woodlandTreesUrl,
};

const loaded = new Map<BakedTreeSet, BakedTree[][]>();
const pending = new Map<BakedTreeSet, Promise<BakedTree[][]>>();

/** Fetch and parse a set once (JSON.parse, not a JS module: it parses in a
 *  fraction of the time and the browser caches the file like any asset). */
export function loadBakedTrees(set: BakedTreeSet): Promise<BakedTree[][]> {
  const done = loaded.get(set);
  if (done) return Promise.resolve(done);
  let job = pending.get(set);
  if (!job) {
    job = fetch(SET_URLS[set])
      .then(async (response) => {
        if (!response.ok) throw new Error(`${set} trees: HTTP ${response.status}`);
        const data = (await response.json()) as { version: number; trees: BakedTree[][] };
        loaded.set(set, data.trees);
        return data.trees;
      })
      .finally(() => pending.delete(set));
    pending.set(set, job);
  }
  return job;
}

export function bakedTreesLoaded(set: BakedTreeSet): boolean {
  return loaded.has(set);
}

function registered(set: BakedTreeSet): BakedTree[][] {
  const trees = loaded.get(set);
  if (!trees) {
    throw new Error(`lagking-tv: the ${set} baked trees are not loaded yet; preload the biome before mounting it`);
  }
  return trees;
}

export function cedarBakedTrees(): BakedTree[][] {
  return registered('cedar');
}

export function woodlandBakedTrees(): BakedTree[][] {
  return registered('woodland');
}
